/**
 * Shared Brand Bible form controls and a generic section header.
 * Page-level headers look up section meta and wrap this header; section
 * panels import these primitives directly so they never import the page.
 */
import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { cn } from '@/lib/utils';

type HeaderIcon = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

export function SectionHeader({
  letter,
  icon: Icon,
  hint,
  title,
  right,
}: {
  letter: string;
  icon: HeaderIcon;
  hint: string;
  title: string;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-3.5">
      <div className="flex items-center gap-3">
        <div className="w-[34px] h-[34px] rounded-lg bg-warm-bg grid place-items-center text-coral flex-shrink-0 border border-border">
          <Icon className="size-4" />
        </div>
        <div>
          <div className="text-[11px] tracking-wider text-muted-foreground uppercase font-semibold">
            <span className="font-mono">{letter}</span> · {hint}
          </div>
          <div className="text-xl font-semibold mt-0.5 -tracking-[0.01em]">{title}</div>
        </div>
      </div>
      {right}
    </div>
  );
}

export function FieldLabel({
  children,
  required,
  hint,
  review,
}: {
  children: ReactNode;
  required?: boolean;
  hint?: string;
  review?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[12.5px] font-semibold text-muted-foreground">
        {children}
        {required && <span className="text-coral ml-0.5">*</span>}
      </label>
      {review ? review : hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

interface TextFieldProps {
  value: string;
  onChange: (v: string) => void;
  /** Fires on blur with the current value — for fields that also need to commit
   * to a second (non-debounced) write path, e.g. the v2 identity singleton. */
  onBlur?: (v: string) => void;
  placeholder?: string;
  prefix?: ReactNode;
  type?: string;
  readOnly?: boolean;
}

export function TextField({ value, onChange, onBlur, placeholder, prefix, type = 'text', readOnly }: TextFieldProps) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-0 rounded-lg bg-card border transition-[border-color,box-shadow] duration-150',
        focused ? 'border-coral ring-[3px] ring-coral/15' : 'border-border',
      )}
    >
      {prefix && <span className="flex text-muted-foreground">{prefix}</span>}
      <input
        type={type}
        readOnly={readOnly}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e.target.value);
        }}
        className="flex-1 border-0 outline-none py-2 text-[14px] bg-transparent min-w-0"
      />
    </div>
  );
}

/**
 * Uncontrolled sibling of TextField for v2-identity-only fields that have no flat
 * legacy echo (dba, founded_year, team_size, booking_url, owner.name/title).
 * Commits on blur only — unlike the legacy draft's debounced autosave, bb_identity's
 * upsertEntry hits the API on every call, so per-keystroke commits would spam it.
 * Callers key this by `identity.entry?.id` so a value that loads in async (v2 fetch
 * resolves after mount) re-mounts the input with the fresh defaultValue.
 */
export function IdentityField({
  value,
  onCommit,
  placeholder,
  prefix,
}: {
  value: string;
  onCommit: (v: string) => void;
  placeholder?: string;
  prefix?: ReactNode;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-0 rounded-lg bg-card border transition-[border-color,box-shadow] duration-150',
        focused ? 'border-coral ring-[3px] ring-coral/15' : 'border-border',
      )}
    >
      {prefix && <span className="flex text-muted-foreground">{prefix}</span>}
      <input
        defaultValue={value}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          if (e.target.value !== value) onCommit(e.target.value);
        }}
        className="flex-1 border-0 outline-none py-2 text-[14px] bg-transparent min-w-0"
      />
    </div>
  );
}

interface TextAreaProps {
  value: string;
  onChange: (v: string) => void;
  /** Fires on blur with the current value — mirrors TextField's onBlur, for
   * fields that also need to commit to a second (non-debounced) write path,
   * e.g. the v2 identity singleton. */
  onBlur?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  readOnly?: boolean;
}

export function TextArea({ value, onChange, onBlur, placeholder, rows = 3, className, readOnly }: TextAreaProps) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      rows={rows}
      readOnly={readOnly}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e.target.value);
      }}
      className={cn(
        'w-full px-3 py-2.5 rounded-lg bg-card border outline-none resize-y min-h-[64px] leading-normal text-[14px]',
        'transition-[border-color,box-shadow] duration-150',
        focused ? 'border-coral ring-[3px] ring-coral/15' : 'border-border',
        className,
      )}
    />
  );
}
